// Client de l'API (`/api`, même origine, cookie de session). Une réponse en erreur lève
// `ApiError` avec le code et le message du serveur ; 403 `password_change_required` et 401
// sont relayés à la session (redirection vers le changement de mot de passe ou la connexion).

export interface Issue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  issues: Issue[];
  constructor(status: number, code: string, message: string, issues: Issue[] = []) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

/** Messages des codes d'erreur courants, quand le serveur n'en fournit pas. */
const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Identifiant ou mot de passe incorrect',
  unauthorized: 'Session expirée, reconnectez-vous',
  forbidden: 'Accès réservé aux administrateurs',
  not_found: 'Introuvable',
  internal_error: 'Erreur du serveur, réessayez',
  invalid_input: 'Saisie à corriger',
};

type Listener = (status: number, code: string) => void;
let listener: Listener | null = null;

/** Session : prévenue des 401 et du mot de passe à changer. */
export function onAuthProblem(callback: Listener | null) {
  listener = callback;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'network', 'Serveur injoignable, vérifiez la connexion');
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json().catch(() => null)) as { error?: string; message?: string; issues?: Issue[] } | null;
  if (!response.ok) {
    const code = data?.error ?? 'internal_error';
    if (response.status === 401 || code === 'password_change_required') listener?.(response.status, code);
    const issues = data?.issues ?? [];
    const message = data?.message ?? (issues.length > 0 ? issues.map((issue) => issue.message).join(' · ') : (MESSAGES[code] ?? `Erreur ${response.status}`));
    throw new ApiError(response.status, code, message, issues);
  }
  return data as T;
}

export const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Erreur inattendue');
